import { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useLocation } from 'react-router-dom';

const TOUR_COMPLETED_KEY = 'visionboard_tour_completed';

export default function OnboardingTour() {
    const location = useLocation();
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    // Only show tour on dashboard
    const isDashboard = location.pathname === '/dashboard';

    useEffect(() => {
        // Check if tour was already completed
        const tourCompleted = localStorage.getItem(TOUR_COMPLETED_KEY);
        if (!tourCompleted && isDashboard) {
            // Small delay to let the page render
            const timer = setTimeout(() => setRun(true), 1000);
            return () => clearTimeout(timer);
        }
    }, [isDashboard]);

    const steps: Step[] = [
        {
            target: 'body',
            placement: 'center',
            content: (
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">🎯 Chào mừng đến VisionBoard!</h2>
                    <p className="text-neutral-600 mb-4">
                        VisionBoard kết hợp <strong>OKR</strong> (Mục tiêu & Kết quả then chốt) với <strong>Kanban</strong> để giúp bạn quản lý mục tiêu và công việc hiệu quả.
                    </p>
                    <p className="text-sm text-neutral-500">
                        Hãy để chúng tôi hướng dẫn bạn qua các tính năng chính!
                    </p>
                </div>
            ),
            disableBeacon: true,
        },
        {
            target: '[href="/dashboard"]',
            content: (
                <div>
                    <h3 className="font-bold text-lg mb-2">📊 Dashboard</h3>
                    <p className="text-neutral-600">
                        Tổng quan về tiến độ OKR và tasks. Xem nhanh những gì cần làm hôm nay.
                    </p>
                </div>
            ),
            placement: 'right',
        },
        {
            target: '[href="/goals"]',
            content: (
                <div>
                    <h3 className="font-bold text-lg mb-2">🎯 Goals (OKR)</h3>
                    <p className="text-neutral-600 mb-3">
                        <strong>OKR = Objectives & Key Results</strong>
                    </p>
                    <ul className="text-sm text-neutral-600 space-y-2">
                        <li>• <strong>Objective</strong>: Mục tiêu lớn bạn muốn đạt được</li>
                        <li>• <strong>Key Result</strong>: Các chỉ số đo lường thành công</li>
                        <li>• <strong>Check-in</strong>: Cập nhật tiến độ định kỳ</li>
                    </ul>
                    <p className="text-sm mt-3 p-2 bg-primary-50 dark:bg-primary-600/20 rounded">
                        💡 VD: Objective "Tăng doanh số" với Key Result "Đạt 1 tỷ doanh thu"
                    </p>
                </div>
            ),
            placement: 'right',
        },
        {
            target: '[href="/boards"]',
            content: (
                <div>
                    <h3 className="font-bold text-lg mb-2">📋 Boards (Kanban)</h3>
                    <p className="text-neutral-600 mb-3">
                        Quản lý công việc theo phương pháp Kanban - kéo thả tasks giữa các cột.
                    </p>
                    <ul className="text-sm text-neutral-600 space-y-1">
                        <li>• <strong>To Do</strong>: Việc cần làm</li>
                        <li>• <strong>In Progress</strong>: Đang làm</li>
                        <li>• <strong>Done</strong>: Hoàn thành</li>
                    </ul>
                    <p className="text-sm mt-3 p-2 bg-success-50 dark:bg-success-600/20 rounded">
                        ✨ Tip: Link tasks với Key Results để theo dõi đóng góp vào mục tiêu!
                    </p>
                </div>
            ),
            placement: 'right',
        },
        {
            target: '[href="/analytics"]',
            content: (
                <div>
                    <h3 className="font-bold text-lg mb-2">📈 Analytics</h3>
                    <p className="text-neutral-600">
                        Xem báo cáo và thống kê về tiến độ OKR, hiệu suất làm việc.
                    </p>
                </div>
            ),
            placement: 'right',
        },
        {
            target: '[href="/feedback"]',
            content: (
                <div>
                    <h3 className="font-bold text-lg mb-2">💬 Feedback</h3>
                    <p className="text-neutral-600">
                        Gửi góp ý, báo lỗi hoặc đề xuất tính năng mới. Chúng tôi luôn lắng nghe!
                    </p>
                </div>
            ),
            placement: 'right',
        },
        {
            target: 'body',
            placement: 'center',
            content: (
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">🚀 Bắt đầu thôi!</h2>
                    <p className="text-neutral-600 mb-4">
                        Bạn đã sẵn sàng sử dụng VisionBoard. Hãy bắt đầu bằng cách:
                    </p>
                    <ol className="text-left text-neutral-600 space-y-2 mb-4">
                        <li><strong>1.</strong> Tạo Objective đầu tiên trong <strong>Goals</strong></li>
                        <li><strong>2.</strong> Thêm Key Results để đo lường thành công</li>
                        <li><strong>3.</strong> Tạo Board và Tasks để quản lý công việc</li>
                        <li><strong>4.</strong> Link Tasks với Key Results</li>
                    </ol>
                    <p className="text-sm text-neutral-500">
                        Bạn có thể xem lại hướng dẫn này trong <strong>Settings</strong>
                    </p>
                </div>
            ),
        },
    ];

    const handleCallback = (data: CallBackProps) => {
        const { status, index, type } = data;

        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRun(false);
            localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
        }

        if (type === 'step:after') {
            setStepIndex(index + 1);
        }
    };

    if (!isDashboard) return null;

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showSkipButton
            showProgress
            scrollToFirstStep
            disableOverlayClose
            callback={handleCallback}
            locale={{
                back: 'Quay lại',
                close: 'Đóng',
                last: 'Hoàn thành',
                next: 'Tiếp theo',
                skip: 'Bỏ qua',
            }}
            styles={{
                options: {
                    primaryColor: '#6366f1',
                    zIndex: 10000,
                },
                tooltip: {
                    borderRadius: '12px',
                    padding: '20px',
                },
                tooltipContainer: {
                    textAlign: 'left',
                },
                buttonNext: {
                    backgroundColor: '#6366f1',
                    borderRadius: '8px',
                    padding: '10px 20px',
                },
                buttonBack: {
                    color: '#6366f1',
                },
                buttonSkip: {
                    color: '#9ca3af',
                },
            }}
        />
    );
}

// Export function to restart tour
export function restartOnboardingTour() {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
    window.location.href = '/dashboard';
}
